import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Plus, Lock, Ban, Gift, Smartphone, Sparkles, Users, Building2, HeartHandshake, Briefcase, Megaphone, Trophy, Check } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useMarket } from "@/lib/market";
import { useAuth } from "@/context/AuthContext";
import { useSitePage, media } from "@/components/public/Blocks";
import { useLogos } from "@/components/public/Showcase";
import { Head, StaticFaq } from "@/components/public/Category";
import { PhoneMockup } from "@/components/LandingSections";
import { Simulator, ChallengeTypes, Features } from "@/components/LandingExplain";
import { HeroBubbles } from "@/components/HeroBubbles";
import { SocialLinks } from "@/components/Socials";
import { Steps, Banter } from "@/pages/Landing";
import { ExperienceCard } from "@/pages/public/ExperiencesPage";
import { FeedCard } from "@/pages/public/FeedPage";

const FAQ = [
  ["Qui peut créer une ligue ?", "Tout le monde. Une fois inscrit grâce à une invitation, tu peux créer ta propre ligue et devenir son organisateur."],
  ["Est-ce que ça coûte quelque chose ?", "Non. Aucune mise, aucune cote, aucun paiement : seulement des points et des défis acceptés librement."],
  ["Et si je rate un soir ?", "Aucun point perdu et ta série n'est pas cassée. Un rappel sur ton téléphone t'évite d'oublier."],
  ["Qui choisit les règles de points ?", "L'organisateur, à la création de la ligue (Détente, Compétition, Expert ou personnalisé). Elles sont figées dès le début de la compétition : personne ne les change en cours de route."],
  ["Jusqu'à quand puis-je faire mon prono ?", "Jusqu'au coup d'envoi de chaque match. Ensuite, le prono est verrouillé."],
  ["Et pour mon entreprise ou ma communauté ?", "Communities et Business donnent une ligue et une app à ton image : logo, couleurs, lots, organisateurs. En entreprise, aucune publicité externe."],
];

const PRO = [
  { to: "/communities", icon: HeartHandshake, label: "Communities", who: "Médias, créateurs, podcasts, fan clubs, associations", items: ["Ta ligue à ton image", "Public, sur invitation ou par code", "Lots et annonces"] },
  { to: "/business", icon: Briefcase, label: "Business", who: "Entreprises, équipes, filiales, séminaires", items: ["Application interne white-label", "Aucune publicité externe", "Engagement agrégé"] },
  { to: "/partners", icon: Megaphone, label: "Partners", who: "Marques, commerces, services, agences", items: ["Encarts intégrés au jeu", "Défis et Partner Leagues", "Reporting agrégé"] },
  { to: "/experiences", icon: Trophy, label: "Experiences", who: "Joueurs et sponsors", items: ["Une ligue dédiée", "Un vrai lot NBA à gagner", "Règlement clair"] },
];

function InviteForm() {
  const [link, setLink] = useState("");
  const nav = useNavigate();
  const go = (e) => {
    e.preventDefault();
    const m = link.trim().match(/invitation\/([A-Za-z0-9_-]+)/) || link.trim().match(/^([A-Za-z0-9_-]{16,})$/);
    if (m) nav(`/invitation/${m[1]}`); else toast.error("Ce lien d'invitation n'est pas valide. Copie le lien complet reçu de l'organisateur.");
  };
  return (
    <form id="invitation" onSubmit={go} className="mpl-rise mt-8 flex max-w-md scroll-mt-24 flex-col gap-2 sm:flex-row" style={{ animationDelay: "240ms" }} data-testid="home-invite-form">
      <input className="p-input" placeholder="Colle ton lien d'invitation" value={link} onChange={(e) => setLink(e.target.value)} aria-label="Lien d'invitation" data-testid="home-invite-input" />
      <button className="p-btn shrink-0 justify-center" data-testid="home-invite-submit">J'ai une invitation <ArrowRight size={16} /></button>
    </form>
  );
}

function HomeHero({ block }) {
  const { user } = useAuth();
  const title = block?.title || "La NBA se vit ensemble.";
  const words = title.split(" ");
  const last = words.pop();
  return (
    <section className="border-b p-line" data-testid="home-hero">
      <div className="mx-auto grid max-w-7xl items-center gap-12 px-5 pb-16 pt-10 md:grid-cols-[1.2fr_1fr] md:pt-16">
        <div>
          <p className="p-eyebrow mpl-rise">{block?.eyebrow || "Les matchs passent. Les rivalités restent."}</p>
          <h1 className="mpl-rise mt-4 font-display text-5xl uppercase leading-[0.92] sm:text-6xl lg:text-7xl" style={{ animationDelay: "80ms" }} data-testid="home-hero-title">
            {words.length ? <>{words.join(" ")} <span className="bg-[#B7F52A] px-1 text-[#080C0D] [box-decoration-break:clone]">{last}</span></> : title}
          </h1>
          <p className="mpl-rise mt-6 max-w-md text-base p-t2" style={{ animationDelay: "160ms" }}>{block?.subtitle || "Chaque soir, choisis les vainqueurs. À la fin de la période, le premier savoure… et le dernier relève le défi de la ligue."}</p>
          {user ? <p className="mt-8 text-sm" data-testid="home-welcome">Content de te revoir, {user.display_name}. <Link to="/tableau-de-bord" className="p-btn ml-2" data-testid="home-resume">Reprendre ma partie <ArrowRight size={15} /></Link></p> : <InviteForm />}
          <a href="#simulateur" className="mt-3 inline-flex items-center gap-1 text-sm font-bold underline decoration-[#B7F52A] decoration-2 underline-offset-4" data-testid="home-sim-link">Essayer le simulateur de points ↓</a>
          <ul className="mt-5 flex flex-wrap gap-2 text-xs" data-testid="home-badges">{[[Gift, "Gratuit"], [Lock, "Sur invitation"], [Ban, "Aucun pari d'argent"], [Smartphone, "Sur ton téléphone"]].map(([I, t]) => (
            <li key={t} className="flex items-center gap-1.5 rounded-full border p-line px-3 py-1.5 font-bold"><I size={13} /> {t}</li>))}</ul>
          <p className="mt-4 flex items-center gap-2 text-xs p-t3"><Lock size={13} /> Ma Petite Ligue fonctionne exclusivement sur invitation.</p>
        </div>
        <div className="relative flex justify-center md:justify-end"><div className="mpl-float"><PhoneMockup /></div><HeroBubbles /></div>
      </div>
    </section>
  );
}

function Audience() {
  const cards = [
    { href: "#particuliers", icon: Users, title: "Particuliers", text: "Entre amis, en famille, entre collègues : ta ligue privée, gratuite, sur invitation.", testId: "home-aud-private" },
    { href: "#professionnels", icon: Building2, title: "Professionnels", text: "Entreprises, communautés, marques : une ligue et une app à votre image.", testId: "home-aud-pro" },
  ];
  return (
    <div className="mx-auto grid max-w-7xl gap-3 px-5 py-10 md:grid-cols-2">{cards.map((c) => (
      <a key={c.title} href={c.href} className="p-surface group flex items-center gap-4 p-5 transition-transform hover:-translate-y-1" data-testid={c.testId}>
        <span className="grid h-12 w-12 shrink-0 place-items-center rounded-full bg-[#B7F52A] text-[#080C0D]"><c.icon size={22} /></span>
        <span className="flex-1"><span className="block text-xl font-bold">Je suis : {c.title}</span><span className="mt-1 block text-sm p-t2">{c.text}</span></span>
        <ArrowRight size={18} className="shrink-0 transition-transform group-hover:translate-x-1" />
      </a>))}</div>
  );
}

function ProSection() {
  return (
    <section id="professionnels" className="scroll-mt-20 border-t p-line" data-testid="home-pro">
      <div className="mx-auto max-w-7xl px-5 py-16">
        <Head eyebrow="Entreprises, communautés, créateurs, marques" title="Une ligue et une app à ton image" text="Ton logo, tes couleurs, tes lots : fais vivre ton équipe, ton audience ou ta marque autour de la NBA. Chaque univers a sa page détaillée." />
        <div className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">{PRO.map((u) => (
          <Link key={u.to} to={u.to} className="p-surface group flex flex-col p-6 transition-transform hover:-translate-y-1" data-testid={`home-pro-${u.label.toLowerCase()}`}>
            <span className="grid h-11 w-11 place-items-center rounded-full bg-[#B7F52A] text-[#080C0D]"><u.icon size={20} /></span>
            <p className="mt-4 text-xl font-bold">{u.label}</p>
            <p className="mt-1 text-sm p-t3">{u.who}</p>
            <ul className="mt-4 flex-1 space-y-1.5">{u.items.map((t) => <li key={t} className="flex items-start gap-2 text-sm"><Check size={15} className="mt-0.5 shrink-0" /> {t}</li>)}</ul>
            <span className="mt-5 inline-flex items-center gap-1 text-sm font-bold underline decoration-[#B7F52A] decoration-2 underline-offset-4">Découvrir <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" /></span>
          </Link>))}</div>
      </div>
    </section>
  );
}

function Teasers() {
  const [market] = useMarket();
  const [exp, setExp] = useState([]);
  const [feed, setFeed] = useState([]);
  useEffect(() => {
    api.get("/experiences", { params: { market } }).then(({ data }) => setExp([...data.live, ...data.upcoming].slice(0, 3))).catch(() => setExp([]));
    api.get("/feed", { params: { market, limit: 4 } }).then(({ data }) => setFeed(data.items)).catch(() => setFeed([]));
  }, [market]);
  return (<>
    {exp.length > 0 && <section className="mx-auto max-w-7xl px-5 py-12" data-testid="home-experiences"><h2 className="font-display text-4xl uppercase">À gagner en ce moment</h2><div className="mt-6 grid gap-4 md:grid-cols-3">{exp.map((e) => <ExperienceCard key={e.id} e={e} />)}</div></section>}
    {feed.length > 0 && <section className="mx-auto max-w-7xl px-5 py-12" data-testid="home-feed"><div className="flex items-end justify-between"><h2 className="font-display text-4xl uppercase">Le Feed</h2><Link to="/feed" className="p-link text-sm font-bold">Tout voir</Link></div><div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">{feed.map((i) => <FeedCard key={i.id} item={i} />)}</div></section>}
  </>);
}

export function Faq({ blocks }) {
  if (!blocks.length) return null;
  return (
    <section className="mx-auto max-w-4xl px-5 py-14" data-testid="pub-faq-cms">
      <div className="border-t p-line">{blocks.map((b) => (
        <details key={b.id} className="group border-b p-line py-4" data-testid={`pub-faq-${b.key}`}>
          <summary className="flex cursor-pointer list-none items-center justify-between gap-3 text-lg font-bold">{b.title}<Plus size={18} className="shrink-0 transition-transform group-open:rotate-45" /></summary>
          <p className="mt-2 text-base p-t2">{b.body}</p>
        </details>))}</div>
    </section>
  );
}

function LogosBand({ blocks }) {
  const auto = useLogos();
  const manual = blocks.filter((b) => b.key.startsWith("logo-") && b.media_url).map((b) => ({ id: b.id, name: b.title, logo: b.media_url, url: b.cta_url }));
  const items = [...manual, ...auto];
  if (!items.length) return null;
  return (
    <section className="border-b p-line" data-testid="home-logos">
      <div className="mx-auto flex max-w-7xl flex-col gap-5 px-5 py-8 md:flex-row md:items-center">
        <p className="shrink-0 text-xs font-bold uppercase tracking-[0.16em] p-t3">Ils jouent déjà avec nous</p>
        <ul className="flex flex-wrap items-center gap-x-8 gap-y-4">{items.map((l) => {
          const img = <img src={media(l.logo)} alt={l.name} title={l.name} className="h-10 w-auto max-w-[140px] object-contain opacity-80 grayscale transition-[filter,opacity] duration-300 hover:opacity-100 hover:grayscale-0" />;
          return <li key={l.id} data-testid={`home-logo-${l.id}`}>{l.url ? <a href={l.url} target="_blank" rel="noreferrer">{img}</a> : img}</li>;
        })}</ul>
      </div>
    </section>
  );
}

export default function Home() {
  const blocks = useSitePage("home") || [];
  const get = (k) => blocks.find((b) => b.key === k);
  return (
    <div data-testid="home-page">
      <HomeHero block={get("hero")} />
      <LogosBand blocks={blocks} />
      <Audience />
      <Banter />
      <div id="particuliers" className="scroll-mt-20" data-testid="home-private">
        <Steps />
        <Simulator />
        <ChallengeTypes />
        <Features />
        <section className="mx-auto max-w-6xl px-5 pb-16">
          <div className="p-surface flex items-start gap-3 p-5 text-sm" data-testid="home-friendly"><Ban className="shrink-0" size={20} />
            <p><b>100 % amical.</b> <span className="p-t2">Les défis (croissants, resto, maillot rival…) sont choisis par la ligue, acceptés par chacun, et une option sans dépense est toujours possible.</span> <Sparkles size={13} className="inline" /></p></div>
        </section>
      </div>
      <ProSection />
      <Teasers />
      <StaticFaq items={FAQ} testId="home-faq" />
      <section className="mx-auto max-w-6xl px-5 pb-16" data-testid="home-socials">
        <Head eyebrow="Suis-nous" title="Rejoins la communauté @mapetiteligue" text="Classements de la semaine, défis les plus fous, nouveautés de l'app : tout passe par nos réseaux." />
        <div className="mt-6 max-w-2xl"><SocialLinks testPrefix="home-follow" /></div>
      </section>
      <section className="border-t p-line" data-testid="home-final">
        <div className="mx-auto flex max-w-6xl flex-col items-start gap-5 px-5 py-16 md:flex-row md:items-center md:justify-between">
          <div><p className="font-display text-4xl uppercase">Prêt à défier ta bande ?</p><p className="mt-1 text-sm p-t2">Tu as reçu une invitation ? Ouvre ton lien et rejoins ta ligue en une minute. Déjà membre ? <Link to="/connexion" className="font-bold underline" data-testid="home-final-login">Connecte-toi</Link>.</p></div>
          <a href="#invitation" className="p-btn shrink-0" data-testid="home-final-cta">J'ai une invitation <ArrowRight size={16} /></a>
        </div>
      </section>
    </div>
  );
}
